import { useEffect, useState } from "react";
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import FacebookIcon from "@mui/icons-material/Facebook";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LinkIcon from "@mui/icons-material/Link";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { useSnackbar } from "../../../Components/SnackBar";
import InputField from "../../../Components/InputField";
import { resolveVars } from "../hooks/useGrowCommunity";
import { copyText } from "../utils";
import { styles as alertStyles } from "../../../Components/Modals/AlertModal/styles";
import { CARD_BORDER, SOFT_BG, TEXT_MAIN, TEXT_SUB, g } from "../styles";

const s = {
  label: { fontSize: "15px", fontWeight: 500, color: TEXT_MAIN, marginBottom: "8px" },
  // Stand-in for the OG link preview WhatsApp/Facebook render from the short link.
  preview: { display: "flex", border: CARD_BORDER, borderRadius: "10px", overflow: "hidden", backgroundColor: SOFT_BG, marginBottom: "20px" },
  previewImage: { width: "104px", minWidth: "104px", height: "78px", objectFit: "cover", display: "block" },
  previewText: { padding: "10px 12px", display: "flex", flexDirection: "column", justifyContent: "center", gap: "2px", minWidth: 0 },
  previewTitle: { fontSize: "14px", fontWeight: 500, color: TEXT_MAIN, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  previewDomain: { fontSize: "12px", color: TEXT_SUB, direction: "ltr", textAlign: "start" },
  channels: {
    display: "grid",
    gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))" },
    gap: "8px",
    marginTop: "20px",
  },
  channel: { ...g.outlineBtn, justifyContent: "flex-start", padding: "8px 12px" },
  whatsapp: { color: "rgba(37, 160, 90, 1)", fontSize: 18 },
  facebook: { color: "rgba(24, 119, 242, 1)", fontSize: 18 },
  plainIcon: { fontSize: 18 },
};

const buildText = (item, lang, t) =>
  t(`share.templates.${item.occasion}`, { ...resolveVars(item.vars, lang), link: item.link });

// Chrome only accepts PNG on the clipboard, so draw the image to a canvas first.
const copyImageToClipboard = (src) =>
  new Promise((resolve, reject) => {
    if (!navigator.clipboard?.write || typeof window.ClipboardItem === "undefined") {
      reject(new Error("Clipboard images not supported"));
      return;
    }
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext("2d").drawImage(img, 0, 0);
      canvas.toBlob((blob) => {
        if (!blob) return reject(new Error("Image conversion failed"));
        navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]).then(resolve, reject);
      }, "image/png");
    };
    img.onerror = reject;
    img.src = src;
  });

const ShareDialog = ({ item, lang, t, onClose, onShared }) => {
  const snackbar = useSnackbar();
  const [text, setText] = useState("");

  useEffect(() => {
    if (item) setText(buildText(item, lang, t));
  }, [item, lang, t]);

  if (!item) return null;

  const title = item.title[lang] ?? item.title.en;
  const shared = () => onShared(item.id);

  const channels = [
    {
      key: "whatsapp",
      icon: <WhatsAppIcon sx={s.whatsapp} />,
      run: () => window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer"),
    },
    {
      key: "facebook",
      icon: <FacebookIcon sx={s.facebook} />,
      // Facebook no longer accepts pre-filled post text — share the link (its
      // preview carries the image) and put the text on the clipboard.
      run: async () => {
        const copied = await copyText(text);
        snackbar(copied ? t("share.dialog.facebook_text_copied") : t("copy_failed"), copied ? "success" : "error");
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(item.link)}`, "_blank", "noopener,noreferrer");
      },
    },
    {
      key: "email",
      icon: <MailOutlineIcon sx={s.plainIcon} />,
      run: () => {
        window.location.href = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(text)}`;
      },
    },
    {
      key: "copy_link",
      icon: <LinkIcon sx={s.plainIcon} />,
      run: async () => {
        const copied = await copyText(item.link);
        snackbar(copied ? t("share.dialog.link_copied") : t("copy_failed"), copied ? "success" : "error");
      },
    },
    {
      key: "copy_image",
      icon: <ImageOutlinedIcon sx={s.plainIcon} />,
      run: () =>
        copyImageToClipboard(item.image).then(
          () => snackbar(t("share.dialog.image_copied"), "success"),
          () => snackbar(t("share.dialog.image_copy_failed"), "error"),
        ),
    },
  ];

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: alertStyles.dialogPaper }}>
      <Box sx={alertStyles.headerBox}>
        <DialogTitle sx={alertStyles.dialogTitle}>{t("share.dialog.title", { title })}</DialogTitle>
        <IconButton onClick={onClose} size="small" aria-label={t("share.dialog.close")}>
          <CloseIcon fontSize="small" sx={alertStyles.closeIcon} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: "24px" }}>
        <Typography sx={s.label}>{t("share.dialog.preview_label")}</Typography>
        <Box sx={s.preview}>
          <Box component="img" src={item.image} alt={title} sx={s.previewImage} />
          <Box sx={s.previewText}>
            <Typography sx={s.previewTitle}>{title}</Typography>
            <Typography sx={s.previewDomain}>keilot.com</Typography>
          </Box>
        </Box>

        <InputField
          label={t("share.dialog.text_label")}
          helperText={t("share.dialog.text_hint")}
          value={text}
          onChange={(e) => setText(e.target.value)}
          multiline
          rows={5}
        />

        <Box sx={s.channels}>
          {channels.map((channel) => (
            <Button
              key={channel.key}
              variant="outlined"
              startIcon={channel.icon}
              onClick={() => {
                channel.run();
                shared();
              }}
              sx={s.channel}
            >
              {t(`share.dialog.${channel.key}`)}
            </Button>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ShareDialog;
